import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaPhone, FaEnvelope } from 'react-icons/fa';
import services from '../../utils/services';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const moreRef = useRef(null);
  const servicesRef = useRef(null);
  const location = useLocation();

  const mainLinks = [
    { name: 'Home', path: '/' },
    { name: 'Booking', path: '/booking' },
    { name: 'Contact', path: '/contact' },
    { name: 'Locations', path: '/locations' },
  ];

  const serviceLinks = services.map((service) => ({
    name: service.name,
    path: `/services/${service.slug}`,
  }));

  const moreLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Blog', path: '/blog' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Feedback', path: '/feedback' },
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms of Service', path: '/terms-of-service' },
  ];

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobileMenuOpen) {
        return;
      }

      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setIsMoreOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMoreOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isActive = (path) => location.pathname === path;

  const isMoreActive = moreLinks.some((link) => isActive(link.path));
  const isServicesActive =
    location.pathname === '/services' ||
    location.pathname.startsWith('/services/');

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMoreOpen(false);
    setIsServicesOpen(false);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-primary-500 text-white text-sm py-2 hidden md:block">
        <div className="container-custom flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a
              href="tel:0466708090"
              className="flex items-center gap-2 hover:text-accent-400 transition-colors"
            >
              <FaPhone className="text-xs" />
              <span>0466708090</span>
            </a>
            <a
              href="mailto:info@theskilledgroup.com"
              className="flex items-center gap-2 hover:text-accent-400 transition-colors"
            >
              <FaEnvelope className="text-xs" />
              <span>info@theskilledgroup.com</span>
            </a>
          </div>
          <div>
            <span>Serving Brisbane and surrounding suburbs</span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
          isScrolled ? 'shadow-lg' : 'shadow-sm'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0 [perspective:900px]" onClick={closeMobileMenu}>
              <img
                src="/logo/theskilledgroup-logo.svg"
                alt="The Skilled Group logo"
                className="tsp-logo-intro h-14 md:h-16 w-auto max-h-full object-contain"
                loading="eager"
              />
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-1">
              {mainLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'bg-primary-50 text-primary-500'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-primary-500'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Services Dropdown */}
              <div className="relative" ref={servicesRef}>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isServicesActive
                      ? 'bg-primary-50 text-primary-500'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-primary-500'
                  }`}
                >
                  Services
                  <FaChevronDown
                    className={`text-xs transition-transform duration-200 ${
                      isServicesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isServicesOpen && (
                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    <Link
                      to="/services"
                      className={`block px-4 py-2.5 text-sm transition-colors duration-200 ${
                        isActive('/services')
                          ? 'bg-primary-50 text-primary-500 font-medium'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-primary-500'
                      }`}
                    >
                      All Services
                    </Link>
                    <div className="my-1 border-t border-gray-100" />
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`block px-4 py-2.5 text-sm transition-colors duration-200 ${
                          isActive(link.path)
                            ? 'bg-primary-50 text-primary-500 font-medium'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary-500'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* More Dropdown */}
              <div className="relative" ref={moreRef}>
                <button
                  onClick={() => setIsMoreOpen(!isMoreOpen)}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isMoreActive
                      ? 'bg-primary-50 text-primary-500'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-primary-500'
                  }`}
                >
                  More
                  <FaChevronDown
                    className={`text-xs transition-transform duration-200 ${
                      isMoreOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isMoreOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    {moreLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`block px-4 py-2.5 text-sm transition-colors duration-200 ${
                          isActive(link.path)
                            ? 'bg-primary-50 text-primary-500 font-medium'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary-500'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* CTA Button */}
              <Link to="/booking" className="btn-primary ml-4 text-sm py-2.5 px-6">
                Book Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto">
            <div className="px-4 py-6 space-y-1">
              {/* Top contact info mobile */}
              <div className="flex flex-col gap-3 pb-4 mb-4 border-b border-gray-200">
                <a
                  href="tel:0466708090"
                  className="flex items-center gap-3 text-gray-600 hover:text-primary-500"
                >
                  <FaPhone className="text-primary-500" />
                  <span>0466708090</span>
                </a>
                <a
                  href="mailto:info@theskilledgroup.com"
                  className="flex items-center gap-3 text-gray-600 hover:text-primary-500"
                >
                  <FaEnvelope className="text-primary-500" />
                  <span>info@theskilledgroup.com</span>
                </a>
              </div>

              {/* Main Links */}
              {mainLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeMobileMenu}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary-50 text-primary-500'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Services Section */}
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isServicesActive
                      ? 'bg-primary-50 text-primary-500'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>Services</span>
                  <FaChevronDown
                    className={`transition-transform duration-200 ${
                      isServicesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isServicesOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    <Link
                      to="/services"
                      onClick={closeMobileMenu}
                      className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${
                        isActive('/services')
                          ? 'bg-primary-50 text-primary-500 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-primary-500'
                      }`}
                    >
                      All Services
                    </Link>
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={closeMobileMenu}
                        className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${
                          isActive(link.path)
                            ? 'bg-primary-50 text-primary-500 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-primary-500'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* More Section */}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => setIsMoreOpen(!isMoreOpen)}
                  className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isMoreActive
                      ? 'bg-primary-50 text-primary-500'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>More</span>
                  <FaChevronDown
                    className={`transition-transform duration-200 ${
                      isMoreOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isMoreOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    {moreLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={closeMobileMenu}
                        className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${
                          isActive(link.path)
                            ? 'bg-primary-50 text-primary-500 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-primary-500'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile CTA */}
              <div className="pt-6">
                <Link
                  to="/booking"
                  className="btn-primary w-full block text-center"
                  onClick={closeMobileMenu}
                >
                  Book Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;